(function () {
  const SELECTOR = '[data-aos]';
  const ACTIVE_CLASS = 'aos-animate';
  const OFFSET_PX = 50;
  const DEFAULT_DURATION = 700;
  const DEFAULT_DELAY = 0;
  const DEFAULT_EASING = 'cubic-bezier(0.215, 0.61, 0.355, 1)';

  class AnimationEngine {
    constructor() {
      this.elements = new Set();
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.observer = null;
      this.mutationObserver = null;

      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    }

    init() {
      if (this.prefersReducedMotion.matches) {
        this.revealAllImmediately();
      } else {
        this.ensureObserver();
        this.observeExisting();
      }

      this.observeMutations();

      if (typeof this.prefersReducedMotion.addEventListener === 'function') {
        this.prefersReducedMotion.addEventListener('change', this.handleReducedMotionChange);
      } else if (typeof this.prefersReducedMotion.addListener === 'function') {
        this.prefersReducedMotion.addListener(this.handleReducedMotionChange);
      }
    }

    ensureObserver() {
      if (this.observer) {
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersection, {
        root: null,
        rootMargin: `0px 0px -${OFFSET_PX}px 0px`,
        threshold: 0,
      });
    }

    observeExisting() {
      const nodes = document.querySelectorAll(SELECTOR);
      nodes.forEach((node) => this.register(node));
    }

    observeMutations() {
      if (this.mutationObserver) {
        return;
      }

      this.mutationObserver = new MutationObserver(this.handleMutations);
      this.mutationObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    handleMutations(mutations) {
      if (this.prefersReducedMotion.matches) {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) {
              return;
            }

            if (node.matches && node.matches(SELECTOR)) {
              node.classList.add(ACTIVE_CLASS);
            }

            const nested = node.querySelectorAll ? node.querySelectorAll(SELECTOR) : [];
            nested.forEach((el) => el.classList.add(ACTIVE_CLASS));
          });
        });
        return;
      }

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches && node.matches(SELECTOR)) {
            this.register(node);
          }

          const nested = node.querySelectorAll ? node.querySelectorAll(SELECTOR) : [];
          nested.forEach((el) => this.register(el));
        });
      });
    }

    register(el) {
      if (!(el instanceof HTMLElement)) {
        return;
      }

      this.applyPerElementStyles(el);

      if (this.elements.has(el)) {
        return;
      }

      this.elements.add(el);
      if (this.observer) {
        this.observer.observe(el);
      }
    }

    applyPerElementStyles(el) {
      const duration = this.parseNumber(el.getAttribute('data-aos-duration'), DEFAULT_DURATION);
      const delay = this.parseNumber(el.getAttribute('data-aos-delay'), DEFAULT_DELAY);
      const easing = (el.getAttribute('data-aos-easing') || DEFAULT_EASING).trim() || DEFAULT_EASING;

      el.style.setProperty('--aos-duration', `${duration}ms`);
      el.style.setProperty('--aos-delay', `${delay}ms`);
      el.style.setProperty('--aos-easing', easing);
    }

    parseNumber(value, fallback) {
      const parsed = Number.parseInt(value || '', 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    }

    parseOnce(el) {
      const raw = (el.getAttribute('data-aos-once') || '').toLowerCase().trim();
      if (raw === 'false') {
        return false;
      }
      return true;
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        const el = entry.target;
        const once = this.parseOnce(el);

        if (entry.isIntersecting) {
          el.classList.add(ACTIVE_CLASS);

          if (once && this.observer) {
            this.observer.unobserve(el);
            this.elements.delete(el);
          }
          return;
        }

        if (!once) {
          el.classList.remove(ACTIVE_CLASS);
        }
      });
    }

    revealAllImmediately() {
      const nodes = document.querySelectorAll(SELECTOR);
      nodes.forEach((el) => {
        this.applyPerElementStyles(el);
        el.classList.add(ACTIVE_CLASS);
      });

      if (this.observer) {
        this.observer.disconnect();
      }
      this.elements.clear();
    }

    handleReducedMotionChange(event) {
      if (event.matches) {
        this.revealAllImmediately();
        return;
      }

      this.ensureObserver();
      this.observeExisting();
    }
  }

  const start = () => {
    const engine = new AnimationEngine();
    engine.init();
    window.__animationEngine = engine;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
